<template>
  <transition>
    <div class="mod-dial lm-dialog lm-category" @click="requestClose" @keydown.esc="onEscape">
      <div
        class="lm-shell"
        role="dialog"
        aria-modal="true"
        aria-labelledby="category-title"
        tabindex="-1"
        ref="dialog"
        @click.stop
      >
        <header class="lm-header">
          <div>
            <h1 id="category-title">{{ getCurrentCategory ? getCurrentCategory.name : $t('CATEGORY_MANAGER_NAME') }}</h1>
            <p>{{ $t('LM_CATEGORY_DESC') }}</p>
          </div>
          <button type="button" class="lm-close" :aria-label="$t('ACCESS_MANAGER_CLOSE')" @click="requestClose">
            <i class="bi bi-x-lg"></i>
          </button>
        </header>
        <div class="lm-scroll">
          <form class="lm-invite" @submit.prevent="updateCategory">
            <label class="lm-field email-field">
              <span>{{ $t('CATEGORY_MANAGER_NAME') }}</span>
              <input
                type="text"
                class="inp-st"
                v-model="categoryName"
                maxlength="25"
                :aria-invalid="!!nameError"
                :placeholder="$t('CATEGORY_MANAGER_NAME')"
              />
            </label>
            <button class="btn-st-red" type="submit" :disabled="saving">
              {{ saving ? $t('LM_SAVING') : $t('LM_SAVE_CHANGES') }}
            </button>
            <p class="lm-error" v-if="nameError">{{ nameError }}</p>
          </form>

          <div class="lm-people-head">
            <span>{{ $t('LM_PAGES_IN_CATEGORY') }} - {{ draftPages.length }}</span>
          </div>
          <form class="lm-invite" @submit.prevent>
            <label class="lm-field email-field">
              <input
                type="search"
                class="inp-st"
                v-model="pageQuery"
                :placeholder="$t('LM_SEARCH_PAGES')"
                :aria-label="$t('LM_SEARCH_PAGES')"
              />
            </label>
          </form>
          <table class="lm-table" v-if="visiblePages.length">
            <thead>
              <tr>
                <th>{{ $t('INTRO_TABS_NAME') }}</th>
                <th>{{ $t('INTRO_TABS_SOCIAL') }}</th>
                <th>{{ $t('ACCESS_MANAGER_ACTION') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="page in visiblePages" :key="page.page_id">
                <td class="lm-page-cell">
                  <img :src="page.owner_avatar" alt="" class="lm-page-logo" />
                  {{ truncateName(page.owner_name) }}
                </td>
                <td>
                  <span :class="`soc-ico soc-${String(page.social || '').toLowerCase()}`"></span>
                </td>
                <td>
                  <button type="button" class="lm-remove" @click="removePageFromDraft(page)">
                    {{ $t('INTRO_TABS_DELETE') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else class="lm-empty">{{ pageQuery ? $t('LM_NO_MATCH') : $t('LM_NO_PAGES') }}</p>

          <div class="lm-access-block">
            <form @submit.prevent="shareAccess" class="lm-invite">
              <label class="lm-field email-field">
                <input
                  type="email"
                  class="inp-st"
                  v-model="shareEmail"
                  :placeholder="$t('ACCESS_MANAGER_EMAIL')"
                  :aria-invalid="!!emailError"
                  :disabled="sharing"
                />
              </label>
              <button class="btn-st-red" type="submit" :disabled="sharing">
                {{ sharing ? $t('LM_GRANTING') : $t('ACCESS_MANAGER_GRANT_ACCESS') }}
              </button>
              <p class="lm-error" v-if="emailError">{{ emailError }}</p>
            </form>
            <table class="lm-table" v-if="getCategoriesAccessList.length">
              <thead>
                <tr>
                  <th>{{ $t('ACCESS_MANAGER_EMAIL') }}</th>
                  <th>{{ $t('ACCESS_MANAGER_ACTION') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(access, index) in getCategoriesAccessList" :key="index">
                  <td>{{ access.granted_to_email }}</td>
                  <td>
                    <button type="button" class="lm-remove" @click="askRemoveAccess(access.granted_to_email)">
                      {{ $t('ACCESS_MANAGER_REMOVE_ACCESS') }}
                    </button>
                    <div
                      v-if="confirm && confirm.kind === 'access' && confirm.email === access.granted_to_email"
                      class="delete-confirm"
                      @click.stop
                    >
                      <p>{{ $t('LM_REMOVE_ACCESS_BODY', { email: access.granted_to_email }) }}</p>
                      <div class="delete-actions">
                        <button type="button" class="btn-cancel" @click="confirm = null">{{ $t('DELETE_CANCEL') }}</button>
                        <button type="button" class="btn-confirm" @click="runConfirm">{{ $t('DELETE_CONFIRM_DELETE') }}</button>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div ref="alertContainer"></div>
          </div>
        </div>
        <footer class="lm-footer">
          <span class="lm-save-note" v-if="isDirty">{{ $t('LM_UNSAVED') }}</span>
          <span>
            <button type="button" class="lm-danger-text" @click="askDelete">
              {{ $t('DELETE_CONFIRM_DELETE') }}
            </button>
            <div v-if="confirm && confirm.kind === 'delete'" class="delete-confirm" @click.stop>
              <p>{{ $t('LM_DELETE_BODY', { name: getCurrentCategory ? getCurrentCategory.name : '' }) }}</p>
              <div class="delete-actions">
                <button type="button" class="btn-cancel" @click="confirm = null">{{ $t('DELETE_CANCEL') }}</button>
                <button type="button" class="btn-confirm" @click="runConfirm">{{ $t('DELETE_CONFIRM_DELETE') }}</button>
              </div>
            </div>
          </span>
          <span>
            <button type="button" class="btn-st-viol" @click="requestClose">{{ $t('ACCESS_MANAGER_CLOSE') }}</button>
            <div v-if="confirm && confirm.kind === 'discard'" class="delete-confirm" @click.stop>
              <p>{{ $t('LM_UNSAVED_BODY') }}</p>
              <div class="delete-actions">
                <button type="button" class="btn-cancel" @click="confirm = null">{{ $t('LM_KEEP_EDITING') }}</button>
                <button type="button" class="btn-confirm" @click="runConfirm">{{ $t('LM_DISCARD') }}</button>
              </div>
            </div>
          </span>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import {
  CATEGORIES_SET_ACCESS_LIST,
  CATEGORIES_SHARE_ACCESS,
  CATEGORIES_REMOVE_ACCESS,
  CATEGORIES_DELETE,
  CATEGORIES_UPDATE,
  CATEGORIES_SET_MANAGER_VISIBLE,
  CATEGORIES_SET_LIST,
  CATEGORIES_SET_CURRENT,
  CATEGORIES_REMOVE_PAGE,
  CATEGORIES_GET_BY_ID,
} from "../../store/actions/categories";

import PageUtils from "@/models/page-utils";

export default {
  name: "CategoryManager",
  computed: {
    ...mapGetters([
      "getCurrentCategory",
      "getCategoriesAccessList",
      "getPages"
    ]),
    categoryPages() {
      if (!this.getCurrentCategory) return [];
      if (this.getCurrentCategory.pages) {
        return this.getCurrentCategory.pages.filter(page => page.status !== 'deleted');
      } else if (this.getCurrentCategory.page_ids) {
        return this.getPages.filter(page => 
          this.getCurrentCategory.page_ids.includes(page.page_id) && page.status !== 'deleted'
        );
      }
      
      return [];
    },
    
    currentCategoryPageIds() {
      return this.draftPages.map(page => page.page_id);
    },
    visiblePages() {
      const query = (this.pageQuery || "").trim().toLowerCase();
      if (!query) return this.draftPages;
      return this.draftPages.filter(page =>
        (page.owner_name || "").toLowerCase().includes(query) ||
        (page.social || "").toLowerCase().includes(query)
      );
    },
    savedPageIds() {
      return this.savedIds;
    },
    isDirty() {
      if ((this.categoryName || "").trim() !== (this.savedName || "")) return true;
      const current = this.currentCategoryPageIds.map(String).sort().join(",");
      const saved = this.savedPageIds.map(String).sort().join(",");
      return current !== saved;
    },
  },
  data() {
    return {
      shareEmail: "",
      categoryName: "",
      savedName: "",
      draftPages: [],
      savedIds: [],
      pageQuery: "",
      nameError: "",
      emailError: "",
      saving: false,
      sharing: false,
      confirm: null,
    };
  },
  mounted() {
    if (this.getCurrentCategory) {
      this.loadDraft(this.getCurrentCategory);
      this.fetchAccessList();
    }
    this.$nextTick(() => {
      if (this.$refs.dialog) this.$refs.dialog.focus();
    });
  },
  watch: {
    getCurrentCategory(newCategory, oldCategory) {
      if (newCategory && (!oldCategory || newCategory.id !== oldCategory.id)) {
        this.loadDraft(newCategory);
        this.fetchAccessList();
      }
    }
  },
  methods: {
    ...mapActions([
      CATEGORIES_SET_ACCESS_LIST,
      CATEGORIES_SHARE_ACCESS,
      CATEGORIES_REMOVE_ACCESS,
      CATEGORIES_DELETE,
      CATEGORIES_UPDATE,
      CATEGORIES_SET_LIST,
      CATEGORIES_REMOVE_PAGE,
      CATEGORIES_GET_BY_ID,
    ]),
    ...mapMutations([
      CATEGORIES_SET_MANAGER_VISIBLE,
      CATEGORIES_SET_CURRENT,
    ]),

    async fetchAccessList() {
      if (this.getCurrentCategory) {
        await this[CATEGORIES_SET_ACCESS_LIST](this.getCurrentCategory.id);
      }
    },

    pageIdsFrom(category) {
      if (!category) return [];
      if (category.pages && category.pages.length) {
        return category.pages.filter(page => page.status !== "deleted").map(page => page.page_id);
      }
      return category.page_ids ? category.page_ids.slice() : [];
    },
    loadDraft(category) {
      this.categoryName = category.name || "";
      this.savedName = category.name || "";
      this.draftPages = this.categoryPages.map(page => ({ ...page }));
      this.savedIds = this.draftPages.map(page => page.page_id);
      this.pageQuery = "";
      this.nameError = "";
    },
    removePageFromDraft(page) {
      this.draftPages = this.draftPages.filter(item => item.page_id !== page.page_id);
    },
    requestClose() {
      if (this.confirm) return;
      if (this.isDirty) {
        this.confirm = { kind: "discard" };
        return;
      }
      this.closeManager();
    },
    onEscape() {
      if (this.confirm) return;
      this.requestClose();
    },
    closeManager() {
      this[CATEGORIES_SET_MANAGER_VISIBLE](false);
    },
    askDelete() {
      this.confirm = { kind: "delete" };
    },
    askRemoveAccess(email) {
      this.confirm = { kind: "access", email };
    },
    async runConfirm() {
      const pending = this.confirm;
      this.confirm = null;
      if (!pending) return;
      if (pending.kind === "discard") this.closeManager();
      else if (pending.kind === "delete") await this.deleteCategory();
      else if (pending.kind === "access") await this.removeAccess(pending.email);
    },

    async shareAccess() {
      const email = (this.shareEmail || "").trim();
      const duplicates = (this.getCategoriesAccessList || []).map(access => access.granted_to_email);
      if (!email) this.emailError = this.$t("LM_EMAIL_REQUIRED");
      else if (!PageUtils.isValidEmail(email)) this.emailError = this.$t("LM_EMAIL_INVALID");
      else if (duplicates.some(item => (item || "").toLowerCase() === email.toLowerCase())) {
        this.emailError = this.$t("LM_EMAIL_DUPLICATE");
      } else this.emailError = "";
      if (this.emailError || this.sharing || !this.getCurrentCategory) return;
      
      this.sharing = true;
      try {
        await this[CATEGORIES_SHARE_ACCESS]({
          categoryId: this.getCurrentCategory.id,
          granted_to_email: email
        });
        
        await this[CATEGORIES_SET_ACCESS_LIST](this.getCurrentCategory.id);
        
        this.shareEmail = "";
        this.showInlineAlert(this.$t('CATEGORY_MANAGER_ACCESS_GRANTED'));
      } catch (error) {
        if (error?.response?.data?.error_code) {
          this.emailError = this.$t(error.response.data.error_code);
          this.showInlineAlert(this.$t(error.response.data.error_code));
        } else {
          this.showInlineAlert(this.$t('CATEGORY_MANAGER_ACCESS_ERROR'));
        }
      } finally {
        this.sharing = false;
      }
    },

    async removeAccess(email) {
      if (!this.getCurrentCategory) return;
      
      try {
        await this[CATEGORIES_REMOVE_ACCESS]({
          categoryId: this.getCurrentCategory.id,
          email: email
        });
        
        // Explicitly refresh the access list to ensure UI updates
        await this[CATEGORIES_SET_ACCESS_LIST](this.getCurrentCategory.id);
        
        this.showInlineAlert(this.$t('CATEGORY_MANAGER_ACCESS_REMOVED'));
      } catch (error) {
        if (error?.response?.data?.error_code) {
          this.showInlineAlert(this.$t(error.response.data.error_code));
        } else {
          this.showInlineAlert(this.$t('CATEGORY_MANAGER_REMOVE_ERROR'));
        }
      }
    },

    async updateCategory() {
      const name = (this.categoryName || "").trim();
      if (!name) {
        this.nameError = this.$t("CREATE_CATEGORY_NAME_REQUIRED");
        return;
      }
      this.nameError = "";
      if (!this.getCurrentCategory || this.saving) return;
      
      this.saving = true;
      try {
        const response = await this[CATEGORIES_UPDATE]({
          id: this.getCurrentCategory.id,
          name,
          page_ids: this.currentCategoryPageIds
        });
        
        if (response && response.category) {
          this[CATEGORIES_SET_CURRENT](response.category);
          this.loadDraft(response.category);
        } else {
          this.savedName = name;
          this.savedIds = this.currentCategoryPageIds.slice();
        }
        
        this.$store.dispatch('alerts/addAlert', {
          message: this.$t('CATEGORY_MANAGER_UPDATED'),
          color: 'success',
          duration: 5000
        });
      } catch (error) {
        if (error?.response?.data?.error_code) {
          this.showInlineAlert(this.$t(error.response.data.error_code));
        } else {
          this.showInlineAlert(this.$t('CATEGORY_MANAGER_UPDATE_ERROR'));
        }
      } finally {
        this.saving = false;
      }
    },

    async deleteCategory() {
      if (!this.getCurrentCategory) return;
      
      try {
        await this[CATEGORIES_DELETE](this.getCurrentCategory.id);
        this[CATEGORIES_SET_MANAGER_VISIBLE](false);
        this[CATEGORIES_SET_CURRENT](null);
        this.$store.dispatch('alerts/addAlert', {
          message: this.$t('CATEGORY_MANAGER_DELETED'),
          color: 'success',
          duration: 5000
        });
      } catch (error) {
        if (error?.response?.data?.error_code) {
          this.showInlineAlert(this.$t(error.response.data.error_code));
        } else {
          this.showInlineAlert(this.$t('CATEGORY_MANAGER_DELETE_ERROR'));
        }
      }
    },

    truncateName(name) {
      if (!name) return '';
      const maxLength = 25;
      return name.length > maxLength ? name.substring(0, maxLength) + '...' : name;
    },

    showInlineAlert(message) {
      this.$alert.show({
        message: message,
        type: 'inline',
        color: '',
        component: this,
        target: this.$refs.alertContainer,
      });
    },
  },
};
</script>

<style>
</style> 